<template>
  <div v-if="billData" class="breakdown-wrap">

    <!-- ── Period & Consumption ────────────────────────────────────────── -->
    <div class="bd-section-label">Period &amp; Consumption</div>
    <div class="bd-meta-grid">
      <div class="bd-meta-item">
        <span class="bd-meta-key">Period</span>
        <span class="bd-meta-val">{{ formattedBillingPeriod }}</span>
      </div>
      <div v-if="showInitialReading" class="bd-meta-item">
        <span class="bd-meta-key">Initial Reading</span>
        <span class="bd-meta-val">{{ formattedInitialReading }} kWh</span>
      </div>
      <div v-if="showFinalReading" class="bd-meta-item">
        <span class="bd-meta-key">Final Reading</span>
        <span class="bd-meta-val">{{ formattedFinalReading }} kWh</span>
      </div>
      <div class="bd-meta-item">
        <span class="bd-meta-key">Total Consumption</span>
        <span class="bd-meta-val success--text font-weight-medium">
          {{ formattedTotalConsumption }} kWh
        </span>
      </div>
      <div v-if="formattedKvaDemand && formattedKvaDemand !== '0'" class="bd-meta-item">
        <span class="bd-meta-key">{{ demandLabel }}</span>
        <span class="bd-meta-val info--text font-weight-medium">
          {{ formattedKvaDemand }} {{ demandUnit }}
        </span>
      </div>
    </div>

    <v-divider class="my-3" />

    <!-- ── Charge Lines ────────────────────────────────────────────────── -->
    <div v-if="billData.calculatedCharges">

      <!-- Base charges -->
      <div class="bd-section-label">Base Charges</div>
      <template v-for="(value, category) in billData.calculatedCharges">
        <div
          v-if="category
            && category !== 'totalCalculated'
            && typeof value === 'number'
            && typeof category === 'string'
            && !isHiddenCharge(category)
            && !isRegulatoryCharge(category)"
          :key="category"
          class="bd-charge-row"
        >
          <div class="bd-charge-name">
            <span class="font-weight-medium">{{ formatCostCategory(category) }}</span>
            <span class="bd-charge-desc">{{ getChargeDescription(category) }}</span>
          </div>
          <div class="bd-charge-amount">
            {{ $formatCurrency(getDisplayChargeAmount(category, value)) }}
            <!-- {{ $currencySymbol }} -->
          </div>
        </div>
      </template>

      <!-- Subtotal -->
      <div class="bd-subtotal-row">
        <span>Subtotal</span>
        <span class="primary--text font-weight-bold">
          {{ formattedSubtotal }} {{ $currencySymbol }}
        </span>
      </div>

      <!-- Regulatory charges -->
      <template v-if="hasRegulatoryCharges">
        <div class="bd-section-label mt-3">Regulatory Charges</div>
        <template v-for="(value, category) in billData.calculatedCharges">
          <div
            v-if="category
              && category !== 'totalCalculated'
              && typeof value === 'number'
              && typeof category === 'string'
              && !isHiddenCharge(category)
              && isRegulatoryCharge(category)"
            :key="`reg-${category}`"
            class="bd-charge-row"
          >
            <div class="bd-charge-name">
              <span class="font-weight-medium">{{ formatCostCategory(category) }}</span>
              <span class="bd-charge-desc">{{ getChargeDescription(category) }}</span>
            </div>
            <div class="bd-charge-amount">
              {{ $formatCurrency(getDisplayChargeAmount(category, value)) }}
              <!-- {{ $currencySymbol }} -->
            </div>
          </div>
        </template>
      </template>

      <!-- Unit cost (TOU only) -->
      <div
        v-if="shouldUseBillingUnitCost && billingUnitCostValue > 0"
        class="bd-charge-row bd-unit-cost-row"
      >
        <div class="bd-charge-name">
          <span class="font-weight-medium">Unit Cost for Billing</span>
          <span class="bd-charge-desc">Used for final bill calculation</span>
        </div>
        <div class="bd-charge-amount info--text font-weight-medium">
          {{ formattedUnitCostForBilling }} {{ $currencySymbol }}/kWh
        </div>
      </div>

      <!-- Total calculated -->
      <div class="bd-total-row">
        <div>
          <div class="font-weight-bold">Total Calculated</div>
          <div v-if="shouldUseBillingUnitCost" class="bd-charge-desc">
            {{ totalCalculationNote }}
          </div>
        </div>
        <div class="success--text font-weight-bold bd-total-amount">
          {{ formattedTotalCalculated }} {{ $currencySymbol }}
        </div>
      </div>

      <!-- Variance comparison -->
      <div
        v-if="billData.actualBillAmount > 0"
        class="bd-variance-row"
        :class="`bd-variance-row--${varianceColor}`"
      >
        <div class="bd-variance-cell">
          <div class="bd-variance-label">Calculated</div>
          <div class="bd-variance-value">
            {{ formattedTotalCalculated }}
            <span class="bd-variance-currency">{{ $currencySymbol }}</span>
          </div>
        </div>
        <div class="bd-variance-cell">
          <div class="bd-variance-label">Actual</div>
          <div class="bd-variance-value">
            {{ formattedActualAmount }}
            <span class="bd-variance-currency">{{ $currencySymbol }}</span>
          </div>
        </div>
        <div class="bd-variance-cell">
          <div class="bd-variance-label">Variance</div>
          <div class="bd-variance-value" :class="varianceTextColor">
            <v-icon x-small :color="varianceIconColor">{{ varianceIcon }}</v-icon>
            {{ formattedVariance }}
            <span class="bd-variance-currency">{{ $currencySymbol }}</span>
          </div>
          <v-chip
            x-small
            :color="varianceColor"
            text-color="white"
            class="mt-1"
          >
            {{ variancePercentage }}% {{ varianceDirection }}
          </v-chip>
        </div>
      </div>
    </div>

    <!-- ── Rates Applied ───────────────────────────────────────────────── -->
    <template v-if="ratesUsed && ratesUsed.length > 0">
      <v-divider class="my-3" />
      <div class="bd-section-label">Rates Applied</div>
      <v-simple-table dense class="bd-rates-table">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Category</th>
              <th class="text-left">Rate</th>
              <th class="text-left">Type</th>
              <th class="text-left">Effective Period</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(rate, index) in ratesUsed"
              :key="`rate-${rate.id}-${index}`"
            >
              <td>
                <v-chip
                  :color="getCategoryColor(getRateCategoryCode(rate))"
                  text-color="white"
                  small
                >
                  <v-icon
                    left
                    small
                  >
                    {{ getCategoryIcon(getRateCategoryCode(rate)) }}
                  </v-icon>
                  {{ formatCostCategory(getRateCategoryCode(rate)) }}
                </v-chip>
              </td>
              <td>{{ formatRate(rate) }}</td>
              <td>{{ rate.rateType }}</td>
              <td>{{ formatEffectivePeriod(rate) }}</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </template>

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import costCategoryMixin from '../../mixins/costCategoryMixin';
import { resolveBillingMode, isTimeOfUseBillingMode } from '../../utils/billingMode';
import {
  getDefaultPercentageDependencies,
  normalizePercentageAppliesTo,
} from '../../utils/chargeMaster';
import {
  calculateTimeOfUseBillingRate,
  calculateTimeOfUseTotalCalculated,
} from '../../utils/timeOfUseBilling';
import appConfig from '../../config/appConfig';

export default {
  name: 'BillBreakdownDisplay',
  mixins: [costCategoryMixin],

  props: {
    billData: {
      type: Object,
      required: true,
    },
    useCurrentRates: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('rateMaster', ['allRates']),

    // Billing Period Information
    formattedBillingPeriod() {
      if (!this.billData.billingPeriod) return 'N/A';
      const start = new Date(this.billData.billingPeriod.startDate).toLocaleDateString();
      const end = new Date(this.billData.billingPeriod.endDate).toLocaleDateString();
      return `${start} - ${end}`;
    },

    formattedInitialReading() {
      return this.billData.consumptionData?.initialReading?.toLocaleString() || '0';
    },

    showInitialReading() {
      return (parseFloat(this.billData.consumptionData?.initialReading) || 0) > 0;
    },

    formattedFinalReading() {
      return this.billData.consumptionData?.finalReading?.toLocaleString() || '0';
    },

    showFinalReading() {
      return (parseFloat(this.billData.consumptionData?.finalReading) || 0) > 0;
    },

    formattedTotalConsumption() {
      return this.billData.consumptionData?.totalConsumption?.toLocaleString() || '0';
    },

    formattedKvaDemand() {
      return this.displayKvaDemand.toLocaleString() || '0';
    },

    demandLabel() {
      return this.shouldUseBillingUnitCost ? 'TOU Demand Total' : 'kVA Demand';
    },

    demandUnit() {
      return this.shouldUseBillingUnitCost ? 'kW' : 'kVA';
    },

    displayKvaDemand() {
      if (!this.shouldUseBillingUnitCost) {
        return parseFloat(this.billData.kvaDemand) || 0;
      }

      const demandBreakdown = this.billData.timeOfUseDemand
        || this.billData.consumptionData?.demandBreakdown
        || {};
      const totalDemand = Object.values(demandBreakdown)
        .reduce((sum, line) => sum + (parseFloat(line?.kw) || 0), 0);

      return totalDemand || parseFloat(this.billData.kvaDemand) || 0;
    },

    billingConsumptionValue() {
      if (this.shouldUseBillingUnitCost) {
        return parseFloat(this.billData.consumptionData?.totalConsumption) || 0;
      }

      const actualKwhFromBill = parseFloat(this.billData.actualKwhFromBill);
      if (!Number.isNaN(actualKwhFromBill) && actualKwhFromBill > 0) {
        return actualKwhFromBill;
      }

      return parseFloat(this.billData.consumptionData?.totalConsumption) || 0;
    },

    formattedBillingConsumption() {
      return this.billingConsumptionValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    totalCalculationNote() {
      const source = this.useCurrentRates ? 'Effective charge total' : 'Saved charge total';
      return `${source} for ${this.formattedBillingConsumption} kWh`;
    },

    displayedBillingConsumptionValue() {
      return Number(this.billingConsumptionValue.toFixed(2));
    },

    billingMode() {
      return resolveBillingMode({
        activeCategories: this.costCategories,
        billData: this.billData,
      });
    },

    shouldUseBillingUnitCost() {
      return appConfig.getBillingCapabilities().useTouUnitCost === true
        && isTimeOfUseBillingMode(this.billingMode);
    },

    billingUnitCostValue() {
      if (!this.shouldUseBillingUnitCost) return 0;

      if (!this.useCurrentRates) {
        const savedUnitCost = parseFloat(this.billData.unitCostForBilling);
        if (!Number.isNaN(savedUnitCost) && savedUnitCost > 0) {
          return savedUnitCost;
        }

        const savedBillingRate = this.getRateByCategory('BILLING_UNIT_COST');
        const savedRateValue = parseFloat(savedBillingRate?.rate);
        if (!Number.isNaN(savedRateValue) && savedRateValue > 0) {
          return savedRateValue;
        }
      }

      const subtotal = this.getSubtotalAmount();
      const units = this.billingConsumptionValue;

      return calculateTimeOfUseBillingRate({
        ...this.billData,
        subtotal,
        calculatedCharges: {
          ...(this.billData.calculatedCharges || {}),
          GROSS_CALCULATED: subtotal,
          TOTAL_UNITS: units,
        },
        consumptionData: {
          ...(this.billData.consumptionData || {}),
          totalConsumption: units,
        },
      });
    },

    calculatedTotalAmount() {
      if (this.shouldUseBillingUnitCost) {
        if (!this.useCurrentRates) {
          return parseFloat(this.billData.calculatedCharges?.totalCalculated) || 0;
        }

        return calculateTimeOfUseTotalCalculated({
          ...this.billData,
          subtotal: this.getSubtotalAmount(),
          calculatedCharges: {
            ...(this.billData.calculatedCharges || {}),
            GROSS_CALCULATED: this.getSubtotalAmount(),
            TOTAL_UNITS: this.billingConsumptionValue,
          },
          consumptionData: {
            ...(this.billData.consumptionData || {}),
            totalConsumption: this.billingConsumptionValue,
          },
        });
      }

      if (!this.useCurrentRates) {
        return parseFloat(this.billData.calculatedCharges?.totalCalculated) || 0;
      }

      const currentTotal = this.getCurrentDisplayedTotalAmount();
      return currentTotal > 0
        ? currentTotal
        : parseFloat(this.billData.calculatedCharges?.totalCalculated) || 0;
    },

    formattedTotalCalculated() {
      return this.$formatCurrency(this.calculatedTotalAmount, { showSymbol: false });
    },

    formattedActualAmount() {
      return this.$formatCurrency(parseFloat(this.billData.actualBillAmount) || 0, {
        showSymbol: false,
      });
    },

    formattedUnitCostForBilling() {
      return this.$formatCurrency(this.billingUnitCostValue, { showSymbol: false });
    },

    formattedSubtotal() {
      return this.$formatCurrency(this.getSubtotalAmount(), { showSymbol: false });
    },

    hasRegulatoryCharges() {
      if (!this.billData.calculatedCharges) {
        return false;
      }

      return Object.entries(this.billData.calculatedCharges).some(([category, value]) => (
        category
        && category !== 'totalCalculated'
        && typeof category === 'string'
        && typeof value === 'number'
        && !this.isHiddenCharge(category)
        && Number.isFinite(value)
        && this.isRegulatoryCharge(category)
      ));
    },

    // Variance Calculations
    variance() {
      const actualAmount = parseFloat(this.billData.actualBillAmount) || 0;
      return actualAmount - this.calculatedTotalAmount;
    },

    formattedVariance() {
      return this.$formatCurrency(Math.abs(this.variance), { showSymbol: false });
    },

    variancePercentage() {
      if (!this.calculatedTotalAmount) return '0';
      const percentage = Math.abs((this.variance / this.calculatedTotalAmount) * 100);
      return percentage.toFixed(2);
    },

    varianceDirection() {
      return this.variance >= 0 ? 'higher' : 'lower';
    },

    varianceColor() {
      const absVariance = Math.abs(this.variance);
      const calculatedAmount = this.calculatedTotalAmount;
      // eslint-disable-next-line max-len
      const variancePercentage = calculatedAmount > 0 ? ((absVariance / calculatedAmount)) * 100 : 0;

      if (variancePercentage > 10) return 'error';
      if (variancePercentage > 5) return 'warning';
      return 'success';
    },

    varianceTextColor() {
      return this.variance >= 0 ? 'error--text' : 'warning--text';
    },

    varianceIconColor() {
      return this.variance >= 0 ? 'error' : 'warning';
    },

    varianceIcon() {
      return this.variance >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down';
    },

    ratesUsed() {
      if (!this.useCurrentRates) {
        return this.billData.ratesUsed || [];
      }

      const categories = Object.keys(this.billData.calculatedCharges || {})
        .filter((category) => category && category !== 'totalCalculated' && !this.isHiddenCharge(category));
      return categories
        .map((category) => this.getRateByCategory(category))
        .filter(Boolean)
        .filter((rate, index, rates) => {
          const rateId = rate.id || rate._id || this.getRateCategoryCode(rate);
          return rates.findIndex((item) => (
            (item.id || item._id || this.getRateCategoryCode(item)) === rateId
          )) === index;
        });
    },

    timeOfUseUnits() {
      return this.billData.timeOfUseUnits
        || this.billData.consumptionData?.touBreakdown
        || {};
    },

    timeOfUseDemand() {
      return this.billData.timeOfUseDemand
        || this.billData.consumptionData?.demandBreakdown
        || {};
    },
  },

  created() {
    if (this.useCurrentRates && (!this.allRates || this.allRates.length === 0)) {
      this.fetchRates().catch((error) => {
        console.error('Error loading current billing rates:', error);
      });
    }
  },

  methods: {
    ...mapActions('rateMaster', ['fetchRates']),

    // Helper methods for dynamic charge display
    getChargeAmount(categoryCode) {
      return this.billData.calculatedCharges?.[categoryCode] || 0;
    },

    getTimeOfUseDemandRateValue(rate = null) {
      const currentRate = parseFloat(rate?.rate);
      return Number.isNaN(currentRate) ? 0 : currentRate;
    },

    getTimeOfUseUnitRateValue({
      rate = null,
      units = 0,
      calculatedCharge = 0,
    } = {}) {
      const currentRate = parseFloat(rate?.rate);
      if (!Number.isNaN(currentRate) && currentRate > 0) {
        return currentRate;
      }

      const unitValue = parseFloat(units) || 0;
      const chargeValue = parseFloat(calculatedCharge) || 0;
      return unitValue > 0 && chargeValue > 0 ? chargeValue / unitValue : 0;
    },

    getTimeOfUseDemandKwValue({
      line = {},
      category = '',
      lineKey = '',
      rate = 0,
    } = {}) {
      const directKwCandidates = [
        line.kw,
        line.maxDemand,
        line.demand,
        line.value,
        line.kvaDemand,
      ];

      const directKwCandidate = directKwCandidates.find((candidate) => {
        const parsed = parseFloat(candidate);
        return !Number.isNaN(parsed) && parsed > 0;
      });

      if (directKwCandidate !== undefined) {
        return parseFloat(directKwCandidate);
      }

      const calculatedCharge = parseFloat(this.billData.calculatedCharges?.[category]) || 0;
      if (calculatedCharge > 0 && rate > 0) {
        return calculatedCharge / rate;
      }

      if (lineKey === 'onPeak') {
        const kvaDemand = parseFloat(this.billData.kvaDemand);
        if (!Number.isNaN(kvaDemand) && kvaDemand > 0) {
          return kvaDemand;
        }
      }

      return 0;
    },

    getTimeOfUseDemandLine(lineKey) {
      const candidates = [
        this.timeOfUseDemand?.[lineKey],
      ];

      const validCandidate = candidates.find((candidate) => {
        if (!candidate || typeof candidate !== 'object') {
          return false;
        }

        const kw = parseFloat(candidate.kw);
        return !Number.isNaN(kw) && kw > 0;
      });

      if (validCandidate) {
        return validCandidate;
      }

      return candidates.find((candidate) => candidate && typeof candidate === 'object') || {};
    },

    resolveTimeOfUseLineKey(categoryUpper, type) {
      if (!categoryUpper || !type) return null;

      const normalizedCategory = String(categoryUpper).toUpperCase();
      const normalizedType = String(type).toUpperCase();

      if (!normalizedCategory.includes(normalizedType)) {
        return null;
      }

      const lines = normalizedType === 'UNIT' ? this.timeOfUseUnits : this.timeOfUseDemand;
      const matchingLine = Object.entries(lines || {}).find(([, line]) => {
        const lineCategory = String(line?.categoryCode || line?.category_code || '').toUpperCase();
        return lineCategory === normalizedCategory;
      });

      if (matchingLine) {
        return matchingLine[0];
      }

      return null;
    },

    isHiddenCharge(category) {
      const hiddenCategories = [
        'TOTAL_UNITS',
        'BILLING_UNIT_COST',
        'TOTAL_UNIT_CHARGES',
        'TOTAL_DEMAND_CHARGES',
        'GROSS_CALCULATED',
      ];
      const normalizedCategory = String(category || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

      return hiddenCategories.some((hiddenCategory) => (
        normalizedCategory === hiddenCategory || normalizedCategory.endsWith(`_${hiddenCategory}`)
      ));
    },

    isRegulatoryCharge(category) {
      // Regulatory charges are typically percentage-based charges
      // Check if the category name contains common regulatory terms
      if (!category || typeof category !== 'string') {
        return false;
      }

      try {
        const regulatoryTerms = [
          'VAT', 'TAX', 'REA', 'EWURA', 'LEVY', 'FEE',
          'NATIONAL_ELECTRIFICATION', 'STREET_LIGHT', 'GETFUND', 'NHIL',
          'KEBS', 'ERC', 'REGULATORY', 'GOVERNMENT',
        ];
        return regulatoryTerms.some((term) => category.toUpperCase().includes(term));
      } catch (error) {
        console.warn('Error checking regulatory charge for category:', category, error);
        return false;
      }
    },

    getChargeDescription(category) {
      if (!category || typeof category !== 'string') return 'Calculated charge';

      try {
        const categoryUpper = category.toUpperCase();
        const rate = this.getRateByCategory(category);
        const rateValue = this.getNumericRate(rate);
        const formattedRate = rateValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 4,
        });
        const totalConsumptionValue = parseFloat(
          this.billData.consumptionData?.totalConsumption
           ?? this.billData.consumptionData?.total_consumption,
        ) || 0;
        const totalConsumption = totalConsumptionValue.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        const billingDays = this.calculateBillingDays();

        const timeOfUseUnitLineKey = this.resolveTimeOfUseLineKey(categoryUpper, 'UNIT');
        const timeOfUseDemandLineKey = this.resolveTimeOfUseLineKey(categoryUpper, 'DEMAND');

        if (timeOfUseUnitLineKey) {
          const lineKey = timeOfUseUnitLineKey;
          const line = this.timeOfUseUnits?.[lineKey] || {};
          const calculatedCharge = parseFloat(this.billData.calculatedCharges?.[category]) || 0;
          const unitsValue = parseFloat(line.units)
            || parseFloat(line.difference)
            || 0;
          const lineRateValue = this.getTimeOfUseUnitRateValue({
            line,
            rate,
            units: unitsValue,
            calculatedCharge,
          });
          // eslint-disable-next-line max-len
          const units = (unitsValue || (lineRateValue > 0 ? (calculatedCharge / lineRateValue) : 0)).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          const lineRate = lineRateValue.toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          });
          return `${units} kWh × ${lineRate} ${this.$currencySymbol}/kWh`;
        }

        if (timeOfUseDemandLineKey) {
          const lineKey = timeOfUseDemandLineKey;
          const line = this.getTimeOfUseDemandLine(lineKey);
          const demandRateValue = this.getTimeOfUseDemandRateValue(rate);
          const kwValue = this.getTimeOfUseDemandKwValue({
            line,
            category: categoryUpper,
            lineKey,
            rate: demandRateValue,
          });
          const kw = kwValue.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          const demandRate = demandRateValue.toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          });
          return `${kw} kW × ${demandRate} ${this.$currencySymbol}/kW`;
        }

        if (
          categoryUpper === 'OIL_SURCHARGE'
        ) {
          const timeOfUseTotalUnits = Object.values(this.timeOfUseUnits).reduce((sum,
            line) => sum + (parseFloat(line?.units) || parseFloat(line?.difference) || 0),
          0);
          const surchargeRate = (parseFloat(rate?.rate) || 0).toLocaleString('en-US', {
            minimumFractionDigits: 4,
            maximumFractionDigits: 4,
          });
          const oilUnits = (timeOfUseTotalUnits || parseFloat(this.billData.consumptionData?.totalConsumption) || 0).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
          return `${oilUnits} kWh × ${surchargeRate} ${this.$currencySymbol}/kWh`;
        }

        if (categoryUpper === 'KWH_FIXED_CHARGE' || categoryUpper.includes('KWH')) {
          const formattedKwhRate = rateValue.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
          });
          return `${totalConsumption} kWh x ${formattedKwhRate} ${this.$currencySymbol}/kWh`;
        }

        if (categoryUpper === 'KVA_PER_UNIT_CHARGE' || categoryUpper.includes('KVA')) {
          return `${this.formattedKvaDemand} kVA x ${formattedRate} ${this.$currencySymbol}/kVA`;
        }

        if (categoryUpper === 'SERVICE_CHARGE' || categoryUpper.includes('SERVICE')) {
          if (rate?.rateType === 'DAILY') {
            return `${formattedRate} ${this.$currencySymbol}/day × ${billingDays} days`;
          }

          const chargeAmount = this.billData.calculatedCharges?.[category] || 0;
          return `Service charge: ${chargeAmount.toLocaleString()} ${this.$currencySymbol}`;
        }

        if (this.isRegulatoryCharge(category)) {
          const baseAmount = this.getPercentageBaseAmount(category);
          return `${this.$formatCurrency(baseAmount)} × ${rate?.rate || '0'}%`;
        }

        if (rate?.rateType === 'PERCENTAGE') {
          const baseAmount = this.getPercentageBaseAmount(category);
          return `${this.$formatCurrency(baseAmount)} × ${rate.rate}%`;
        }

        if (rate?.rateType === 'DAILY') {
          return `${formattedRate} ${this.$currencySymbol}/day × ${billingDays} days`;
        }

        if (rate?.rate !== undefined && rate?.rate !== null) {
          return `${totalConsumption} kWh × ${formattedRate} ${this.$currencySymbol}/kWh`;
        }

        return 'Calculated from configured rate';
      } catch (error) {
        console.warn('Error getting charge description for category:', category, error);
        return 'Calculated from configured rate';
      }
    },

    getPercentageBaseAmount(category) {
      if (!this.billData.calculatedCharges) return 0;

      const dependencies = this.getPercentageDependencies(category);
      if (dependencies.length) {
        const dependencyTotal = dependencies.reduce((sum, dependencyCategory) => (
          sum + this.getDisplayChargeAmount(
            dependencyCategory,
            this.billData.calculatedCharges?.[dependencyCategory] || 0,
          )
        ), 0);

        if (dependencyTotal > 0) {
          return dependencyTotal;
        }
      }

      const percentageAmount = this.billData.calculatedCharges[category] || 0;
      const rate = this.getRateByCategory(category);

      if (!rate || !rate.rate || percentageAmount === 0) return 0;

      return ((percentageAmount * 100)) / rate.rate;
    },

    getPercentageDependencies(category) {
      const categoryConfig = this.costCategories.find((item) => (
        (item.category_code || item.categoryCode || item.code) === category
      ));
      const configuredDependencies = this.normalizePercentageAppliesTo(
        categoryConfig?.percentage_applies_to || categoryConfig?.percentageAppliesTo,
      );

      return configuredDependencies.length
        ? configuredDependencies
        : getDefaultPercentageDependencies(category);
    },

    normalizePercentageAppliesTo(appliesTo) {
      return normalizePercentageAppliesTo(appliesTo);
    },

    getDisplayChargeAmount(category, savedAmount = 0) {
      if (!this.useCurrentRates) {
        return savedAmount;
      }

      const currentAmount = this.calculateCurrentChargeAmount(category);
      return currentAmount !== null ? currentAmount : savedAmount;
    },

    calculateCurrentChargeAmount(category) {
      if (!category || typeof category !== 'string') return null;

      const categoryUpper = category.toUpperCase();
      const rate = this.getRateByCategory(category);
      const rateValue = this.getNumericRate(rate);
      const totalConsumptionValue = parseFloat(
        this.billData.consumptionData?.totalConsumption
        ?? this.billData.consumptionData?.total_consumption,
      ) || 0;

      const timeOfUseUnitLineKey = this.resolveTimeOfUseLineKey(categoryUpper, 'UNIT');
      if (timeOfUseUnitLineKey && rateValue > 0) {
        const line = this.timeOfUseUnits?.[timeOfUseUnitLineKey] || {};
        const units = parseFloat(line.units) || parseFloat(line.difference) || 0;
        return units * rateValue;
      }

      const timeOfUseDemandLineKey = this.resolveTimeOfUseLineKey(categoryUpper, 'DEMAND');
      if (timeOfUseDemandLineKey && rateValue > 0) {
        const line = this.getTimeOfUseDemandLine(timeOfUseDemandLineKey);
        const kw = Math.max(parseFloat(line?.kw) || 0, 0);
        return kw * rateValue;
      }

      if (categoryUpper === 'OIL_SURCHARGE' && rateValue > 0) {
        const timeOfUseTotalUnits = Object.values(this.timeOfUseUnits).reduce((sum,
          line) => sum + (parseFloat(line?.units) || parseFloat(line?.difference) || 0),
        0);
        return (timeOfUseTotalUnits || totalConsumptionValue) * rateValue;
      }

      if ((categoryUpper === 'KWH_FIXED_CHARGE' || categoryUpper.includes('KWH')) && rateValue > 0) {
        return totalConsumptionValue * rateValue;
      }

      if ((categoryUpper === 'KVA_PER_UNIT_CHARGE' || categoryUpper.includes('KVA')) && rateValue > 0) {
        return this.displayKvaDemand * rateValue;
      }

      if (categoryUpper === 'SERVICE_CHARGE' || categoryUpper.includes('SERVICE')) {
        if (rate?.rateType === 'DAILY' && rateValue > 0) {
          return rateValue * this.calculateBillingDays();
        }
        return rateValue > 0 ? rateValue : null;
      }

      if ((this.isRegulatoryCharge(category) || rate?.rateType === 'PERCENTAGE') && rateValue > 0) {
        const baseAmount = this.getPercentageBaseAmount(category);
        return (baseAmount * rateValue) / 100;
      }

      return null;
    },

    getCurrentDisplayedTotalAmount() {
      return Object.entries(this.billData.calculatedCharges || {}).reduce(
        (sum, [category, value]) => {
          if (category
            && category !== 'totalCalculated'
            && typeof category === 'string'
            && typeof value === 'number'
            && !this.isHiddenCharge(category)) {
            return sum + this.getDisplayChargeAmount(category, value);
          }
          return sum;
        },
        0,
      );
    },

    getSubtotalAmount() {
      // Calculate subtotal from all base charges (non-regulatory charges)
      return Object.entries(this.billData.calculatedCharges || {}).reduce(
        (sum, [category, value]) => {
          if (category
            && category !== 'totalCalculated'
            && typeof category === 'string'
            && typeof value === 'number'
            && !this.isHiddenCharge(category)
            && !this.isRegulatoryCharge(category)) {
            return sum + this.getDisplayChargeAmount(category, value);
          }
          return sum;
        },
        0,
      );
    },

    calculateBillingDays() {
      // Calculate the number of days in the billing period
      if (!this.billData.billingPeriod) return 30; // Default fallback

      const startDate = new Date(this.billData.billingPeriod.startDate);
      const endDate = new Date(this.billData.billingPeriod.endDate);
      const diffTime = Math.abs(endDate - startDate);
      return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
    },

    getRateByCategory(category) {
      if (!this.useCurrentRates) {
        return (this.billData.ratesUsed || []).find((rate) => (
          this.getRateCategoryCode(rate) === category
        )) || null;
      }

      const billingDate = this.getRateEffectiveDate();
      const categoryCode = String(category || '');
      const matchingRates = (this.allRates || [])
        .filter((rate) => {
          const costCategory = this.getRateCategoryCode(rate);
          const isActive = rate.isActive !== false && rate.is_active !== false;
          return isActive && costCategory === categoryCode;
        })
        .sort((a, b) => new Date(b.effectiveFrom || b.effective_from)
          - new Date(a.effectiveFrom || a.effective_from));

      return matchingRates.find((rate) => this.isRateEffectiveForDate(rate, billingDate)) || null;
    },

    getRateCategoryCode(rate = {}) {
      return rate.costCategory
        || rate.cost_category
        || rate.categoryCode
        || rate.category_code
        || '';
    },

    getRateEffectiveDate() {
      return new Date(
        this.billData.billingPeriod?.endDate
        || this.billData.billingPeriod?.startDate
        || this.billData.consumptionData?.billingPeriod?.endDate
        || Date.now(),
      );
    },

    isRateEffectiveForDate(rate = {}, date = new Date()) {
      const effectiveFrom = new Date(rate.effectiveFrom || rate.effective_from);
      const effectiveTo = new Date(rate.effectiveTo || rate.effective_to);
      const checkTime = date.getTime();
      return effectiveFrom.getTime() <= checkTime && effectiveTo.getTime() >= checkTime;
    },

    getNumericRate(rate = null) {
      const parsed = parseFloat(rate?.rate);
      return Number.isNaN(parsed) ? 0 : parsed;
    },

    formatRate(rate) {
      if ((rate.rateType || rate.rate_type) === 'PERCENTAGE') {
        return `${rate.rate}%`;
      }
      return `${parseFloat(rate.rate || 0).toLocaleString()} ${this.$currencySymbol}`;
    },

    formatEffectivePeriod(rate) {
      const start = new Date(rate.effectiveFrom || rate.effective_from).toLocaleDateString();
      const end = new Date(rate.effectiveTo || rate.effective_to).toLocaleDateString();
      return `${start} - ${end}`;
    },
  },
};
</script>

<style scoped>
/* ── Wrapper ─────────────────────────────────────────────────────────── */
.breakdown-wrap {
  padding: 16px;
}

/* ── Section labels ──────────────────────────────────────────────────── */
.bd-section-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  opacity: 0.5;
  margin-bottom: 8px;
}

/* ── Period / consumption meta grid ─────────────────────────────────── */
.bd-meta-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
}

.bd-meta-item {
  display: flex;
  flex-direction: column;
  min-width: 120px;
}

.bd-meta-key {
  font-size: 0.72rem;
  opacity: 0.55;
  line-height: 1.3;
}

.bd-meta-val {
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
}

/* ── Charge rows ─────────────────────────────────────────────────────── */
.bd-charge-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.bd-charge-name {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding-right: 12px;
  font-size: 0.875rem;
}

.bd-charge-desc {
  font-size: 0.72rem;
  opacity: 0.55;
  margin-top: 1px;
}

.bd-charge-amount {
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  text-align: right;
}

.bd-unit-cost-row {
  background: rgba(var(--v-info-base), 0.04);
  border-radius: 4px;
  padding: 6px 8px;
  margin-top: 4px;
}

/* ── Subtotal row ────────────────────────────────────────────────────── */
.bd-subtotal-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0 4px;
  font-size: 0.875rem;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin-top: 4px;
}

/* ── Total row ───────────────────────────────────────────────────────── */
.bd-total-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 10px 0 8px;
  margin-top: 4px;
  border-top: 2px solid rgba(0, 0, 0, 0.12);
}

.bd-total-amount {
  font-size: 1.1rem;
  white-space: nowrap;
}

/* ── Variance row ────────────────────────────────────────────────────── */
.bd-variance-row {
  display: flex;
  gap: 0;
  margin-top: 12px;
  border-radius: 6px;
  border-left: 4px solid transparent;
  overflow: hidden;
}

.bd-variance-row--success { border-left-color: #4caf50; }
.bd-variance-row--warning { border-left-color: #ff9800; }
.bd-variance-row--error   { border-left-color: #f44336; }

.bd-variance-cell {
  flex: 1;
  padding: 10px 12px;
  border-right: 1px solid rgba(0, 0, 0, 0.07);
  display: flex;
  flex-direction: column;
}

.bd-variance-cell:last-child {
  border-right: none;
}

.bd-variance-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.5;
  margin-bottom: 3px;
}

.bd-variance-value {
  font-size: 0.95rem;
  font-weight: 600;
}

.bd-variance-currency {
  font-size: 0.72rem;
  opacity: 0.6;
  margin-left: 2px;
}

/* ── Rates table ─────────────────────────────────────────────────────── */
.bd-rates-table {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.bd-rates-table th {
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  opacity: 0.6;
}

.bd-rates-table td {
  font-size: 0.82rem !important;
}

</style>
